import React from 'react';
import { FileText, Scale, Shield } from 'lucide-react';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-600 rounded mb-4">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl font-black text-white mb-4 font-inter uppercase">
            Terms & Conditions
          </h1>
          <p className="text-xl text-gray-300 font-inter">
            Lawman Commercial Services Limited - General Terms and Conditions
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Last updated: January 2025
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          
          {/* Introduction */}
          <div className="mb-8">
            <p className="text-gray-700 leading-relaxed">
              These General Terms and Conditions of Sale ("Terms and Conditions") govern the sale of Vehicles, Goods and Services by 
              <strong> Lawman Commercial Services Limited</strong> ("Seller") to the individual or entity purchasing such products or services ("Buyer"). 
              These Terms and Conditions are incorporated into and form part of all quotations, order confirmations, invoices, and contracts for sale issued by the Seller.
            </p>
          </div>

          {/* Section 1: Definitions */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">1</span>
              Definitions
            </h2>
            <div className="pl-11">
              <p className="text-gray-700 mb-3">In these Terms and Conditions, the following terms shall have the meanings assigned to them:</p>
              <ul className="space-y-2 text-gray-700">
                <li><strong>Seller</strong> means Lawman Commercial Services Limited.</li>
                <li><strong>Buyer</strong> means the individual or entity purchasing the Vehicle/Goods/Services.</li>
                <li><strong>Vehicle/Goods/Services</strong> means the items or services described within the applicable Sales Order/Invoice.</li>
                <li><strong>Purchase Price</strong> means the total amount payable by the Buyer for the Vehicle/Goods/Services.</li>
              </ul>
            </div>
          </section>

          {/* Section 2: Sale of Vehicle/Goods/Services */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">2</span>
              Sale of Vehicle/Goods/Services
            </h2>
            <div className="pl-11">
              <p className="text-gray-700">
                The Seller agrees to sell, and the Buyer agrees to purchase, the Vehicle/Goods/Services described in the Sales Order/Invoice, 
                subject to these Terms and Conditions.
              </p>
            </div>
          </section>

          {/* Section 3: Purchase Price and Payment */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">3</span>
              Purchase Price and Payment
            </h2>
            <div className="pl-11 space-y-3">
              <p className="text-gray-700">The Buyer shall pay the Purchase Price specified in the Sales Order/Invoice.</p>
              <p className="text-gray-700">
                If no acceptance terms are agreed in writing, <strong>full payment is required in advance</strong> before despatch 
                or collection of any Vehicle/Goods/Services.
              </p>
              <p className="text-gray-700">A handling fee of <strong>10%</strong> shall apply to Returned Parts that were correctly supplied.</p>
              <p className="text-gray-700">Specific acceptance terms may be confirmed separately by mutual agreement in writing.</p>
            </div>
          </section>

          {/* Section 4: Delivery */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">4</span>
              Delivery
            </h2>
            <div className="pl-11 space-y-3">
              <p className="text-gray-700"><strong>Delivery Date:</strong> Delivery will occur on a mutually agreed date following receipt of full payment.</p>
              <p className="text-gray-700"><strong>Delivery Location:</strong> Delivery shall be made to the address specified by the Buyer.</p>
              <p className="text-gray-700"><strong>Risk of Loss:</strong> Risk of loss or damage passes to the Buyer upon delivery.</p>
              <p className="text-gray-700">
                <strong>Failure to take delivery/collect:</strong> If the Vehicle remains uncollected for 42 days after notification of completion, 
                a storage fee of £5 per day will apply. Storage fees must be paid in full prior to release. If the vehicle is not collected 
                within 180 days after payment of the storage fees, the Seller may exercise a possessory lien and sell the vehicle to recover outstanding amounts.
              </p>
            </div>
          </section>

          {/* Section 5: Inspection and Acceptance */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">5</span>
              Inspection and Acceptance
            </h2>
            <div className="pl-11 space-y-3">
              <p className="text-gray-700">
                The Buyer must inspect the Vehicle/Goods/Services upon delivery or installation and, where applicable, in the presence of the carrier.
              </p>
              <p className="text-gray-700">
                The Buyer has <strong>5 calendar days</strong> from delivery to notify the Seller in writing of any defects or nonconformities.
              </p>
              <p className="text-gray-700">
                If no notice is received within the 5-day period, the Vehicle/Goods/Services will be deemed accepted by the Buyer, 
                and no refund of payment will be permitted.
              </p>
            </div>
          </section>

          {/* Section 6: Warranty */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">6</span>
              Warranty
            </h2>
            <div className="pl-11 space-y-3">
              <p className="text-gray-700">
                <strong>Manufacturer's Warranty:</strong> Where applicable, the Vehicle/Goods/Services are covered by manufacturer's warranty. 
                The Seller acts solely as an agent for warranty claims unless otherwise agreed in writing.
              </p>
              <p className="text-gray-700">
                <strong>Limited Seller Warranty:</strong> The Seller warrants that the Vehicle/Goods/Services are free from defects 
                in material and workmanship for a period of <strong>three (3) months</strong> from the date of delivery, unless otherwise agreed in writing.
              </p>
              <p className="text-gray-700">
                <strong>Exclusions:</strong> This warranty does not cover damage caused by misuse, accidents, or unauthorized modifications 
                by the Buyer, or their agents. No claims for consequential labour or installation charges will be entertained.
              </p>
              <p className="text-gray-700">
                <strong>Remedy:</strong> The Seller's sole obligation under this warranty is, at its discretion, to repair or replace the defective item.
              </p>
            </div>
          </section>

          {/* Section 7: Limitation of Liability */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">7</span>
              Limitation of Liability
            </h2>
            <div className="pl-11">
              <p className="text-gray-700">
                The Seller shall not be liable for any indirect, incidental, or consequential damages arising from or related to 
                the sale or use of the Vehicle/Goods/Services.
              </p>
            </div>
          </section>

          {/* Section 8: Governing Law */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">8</span>
              Governing Law and Jurisdiction
            </h2>
            <div className="pl-11">
              <p className="text-gray-700">
                These Terms and Conditions shall be governed by and construed in accordance with the <strong>laws of England & Wales</strong>. 
                Any disputes shall be subject to the exclusive jurisdiction of the courts of England & Wales.
              </p>
            </div>
          </section>

          {/* Section 9: Dispute Resolution */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">9</span>
              Dispute Resolution
            </h2>
            <div className="pl-11">
              <p className="text-gray-700">
                The parties agree to attempt in good faith to resolve any dispute or claim arising out of or in connection with these 
                Terms and Conditions through negotiation. If the dispute cannot be resolved within 30 days of written notice from one party 
                to the other, the parties shall consider resolving the matter by mediation in accordance with the Centre for Effective 
                Dispute Resolution (CEDR) Model Mediation Procedure before resorting to court proceedings. This clause does not prevent 
                either party from seeking injunctive relief or other urgent legal remedies.
              </p>
            </div>
          </section>

          {/* Section 10: Force Majeure */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">10</span>
              Force Majeure
            </h2>
            <div className="pl-11 space-y-3">
              <p className="text-gray-700">
                The Seller shall not be liable for any delay or failure in performing its obligations under this agreement if such delay 
                or failure results from events, circumstances, or causes beyond its reasonable control. These may include (but are not limited to) 
                acts of God, natural disasters, pandemics, wars, terrorism, civil unrest, government restrictions, strikes, or failures of suppliers or subcontractors.
              </p>
              <p className="text-gray-700">
                In such cases, the time for performance shall be extended for a period equal to the delay caused by such events.
              </p>
            </div>
          </section>

          {/* Section 11: Data Protection */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">11</span>
              Data Protection and GDPR
            </h2>
            <div className="pl-11 space-y-3">
              <p className="text-gray-700">
                The Seller will process any personal data provided by the Buyer in accordance with the UK General Data Protection Regulation (UK GDPR), 
                the Data Protection Act 2018, and any other applicable data protection legislation.
              </p>
              <p className="text-gray-700">
                The Buyer's personal data will be used only for the purposes of fulfilling the contract, managing the customer relationship, 
                and complying with legal obligations.
              </p>
              <p className="text-gray-700">
                The Seller shall take appropriate technical and organisational measures to safeguard such data and shall not disclose it to 
                third parties without lawful basis or the Buyer's prior consent, unless required by law.
              </p>
            </div>
          </section>

          {/* Section 12: Miscellaneous */}
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-black mb-4 flex items-center">
              <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3">12</span>
              Miscellaneous
            </h2>
            <div className="pl-11 space-y-3">
              <p className="text-gray-700">
                <strong>Entire Agreement:</strong> This agreement constitutes the entire understanding between the Seller and Buyer, 
                superseding any prior communications or agreements.
              </p>
              <p className="text-gray-700">
                <strong>Amendments:</strong> Any amendment must be made in writing and signed by both parties.
              </p>
              <p className="text-gray-700">
                <strong>Severability:</strong> If any provision is determined to be invalid or unenforceable, the remaining provisions 
                will continue in full force and effect.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <div className="border-t border-gray-200 pt-8">
            <div className="bg-orange-50 rounded-lg p-6">
              <h3 className="text-lg font-bold text-black mb-4 flex items-center">
                <Scale className="h-5 w-5 text-orange-600 mr-2" />
                Legal Questions?
              </h3>
              <p className="text-gray-700 mb-4">
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="space-y-2 text-gray-700">
                <p><strong>Lawman Commercial Services Limited</strong></p>
                <p>31 Merylees Industrial Est</p>
                <p>Desford, Leicestershire LE9 9FS</p>
                <p>Email: <a href="mailto:Sales@lawmancommercials.com" className="text-orange-600 hover:text-orange-700">Sales@lawmancommercials.com</a></p>
                <p>Phone: <a href="tel:01530231235" className="text-orange-600 hover:text-orange-700">01530 231 235</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsPage;